<template>
  <div 
    ref="sidebarRoot"
    class="sidebar-root flex flex-col h-screen"
    :class="[
      currentRouteGroup ? `${currentRouteGroup}-gradient` : '',
      { 'collapsed': getSidebarCollapsed, 'pointer-events-none': isCollapseTransitioning }
    ]"
    :style="{'--collapse-transition-duration': `${getSidebarCollapseDuration}ms`}"
  >
    <!-- LOGO & COLLAPSE BUTTON -->
    <div class="sidebar-header flex-shrink-0">
      <div class="logo flex-shrink-0">
        <ForeplayLogoSidebarIcon />
        <div v-if="getGuestStatus.isGuestOnly" class="guest-tag">
          <BaseText type="label" size="xs">Guest</BaseText>
        </div>
      </div>
      <button class="collapse-btn p-1 text-neutral-alpha-650 transition-colors duration-100 hover:text-white"
      @click="toggleSidebarCollapse">
        <CollapseIcon :isCollapsed="getSidebarCollapsed" />
      </button>
    </div>
    <!-- PRODUCT SELECTOR -->
    <div class="product-selector w-full flex flex-col flex-shrink-0 mt-3"
    @mouseenter="handleProductSwitcherHover" @mouseleave="handleProductSwitcherUnhover">
      <div class="flex items-center gap-0.5">
        <router-link
          v-for="product in productRoutes" :key="`product-button-${product.key}`"
          :to="{ name: product.routeName }"
          ref="productButton"
          class="product-button"
          :class="{ 'show-active': !productSwitcherHovered && currentRouteGroup === product.key }"
          @mouseenter.native="hoveredProduct = product.key, lastHoveredProduct = product.key, labeledProduct = product.key"
          @mouseleave.native="hoveredProduct = null"
        >
          <!-- Locked Product Overlay -->
          <div v-if="lockedProductMap[product.key]" class="w-10 h-10 lock-overlay"
          :style="{ opacity: currentRouteGroup === product.key || hoveredProduct === product.key ? '100%' : '44%' }">
            <LockedIcon class="w-5 h-5 transition-colors text-white" />
          </div>
          <AnimatedProductNavIcon 
            :product="product.key"
            :key="`${product.key}-anim-icon`"
            :isLocked="lockedProductMap[product.key]" 
            :isHovered="hoveredProduct === product.key" 
            :isCollapseTransitioning="isCollapseTransitioning"
          />
        </router-link>
      </div>
      <!-- (EXPANDED SIDEBAR) Animated Product Name/Hotkey Label  -->
      <transition name="product-label">
        <div v-if="(labeledProduct || productSwitcherHovered) && !getSidebarCollapsed" 
        class="relative w-full h-8 overflow-hidden" @mouseenter="handleProductSwitcherUnhover">
          <div class="product-label-container cursor-default" :class="{ 'disable-transitions': !doAnimLabel }"
          :style="{ transform: `translateX(${labelOffsetDistance}px)` }">
            <div class="product-label pl-2 py-0.5 pr-0.5 rounded-lg bg-neutral-alpha-200"
            :style="{ width: productNameWidth }">
              <transition-group name="product-label-swap" :css="doAnimLabel">
                <BaseText v-for="key in productKeys" v-if="labeledProduct === key || (!labeledProduct && key === 'library')" 
                :key="`product-name-${key}`" type="label" size="sm" class="product-text text-white whitespace-nowrap">
                  {{ lockedProductMap[key] ? `Unlock ${productLabelMap?.[key]?.name ?? ''}` : productLabelMap?.[key]?.name ?? '' }}
                </BaseText>
              </transition-group>
              <div class="product-hotkey rounded-md" :style="{ width: productHotkeyWidth }">
                <transition-group name="product-hotkey-swap" :css="doAnimLabel">
                  <BaseText v-for="key in productKeyShortcuts" v-if="(labeledProduct === key || (!labeledProduct && key === 'library'))" 
                  :key="`product-hotkey-${key}`" type="label" size="sm" class="text-white px-1.5 py-0.5">
                    {{ productLabelMap?.[key]?.hotkey ?? '⌘L' }}
                  </BaseText>
                </transition-group>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <!-- (COLLAPSED SIDEBAR) Animated Product Name/Hotkey Label  -->
    <transition name="collapsed-product-label">
      <div v-if="productSwitcherHovered && getSidebarCollapsed" class="collapsed-product-label px-2 py-1.5 shadow-md"
      :style="{ width: collapsedProductLabelMeta.width, transform: `translateY(${collapsedProductLabelMeta.yOffset}px)` }">
        <transition-group name="collapsed-label-text-swap">
          <div v-for="key in productKeys" v-if="lastHoveredProduct === key" :key="`collapsed-product-name-${key}`"
          class="collapsed-label-text w-full flex flex-col gap-1 p-1">
            <div class="flex items-center gap-2">
              <BaseText type="label" size="sm" class="text-white">
                {{ lockedProductMap[key] ? `Unlock ${productLabelMap?.[key]?.name ?? ''}` : productLabelMap?.[key]?.name ?? '' }}
              </BaseText>
              <BaseText type="label" size="sm" class="text-neutral-alpha-650">
                {{ productLabelMap?.[key]?.hotkey ?? '' }}
              </BaseText>
            </div>
            <BaseText type="body" size="sm" class="text-neutral-alpha-650">
              {{ productLabelMap?.[key]?.description ?? '' }}
            </BaseText>
          </div>
        </transition-group>
      </div>
    </transition>
    <!-- LENS ACCOUNT SWITCHER (Lens Only) -->
    <transition name="fade-mount">
      <div v-if="sidebarLensState?.lensId" class="relative block mt-3" v-on-clickaway="() => lensSwitcherExpanded = false">
        <!-- Expansion Button -->
        <button class="lens-switcher-button flex transition-colors duration-100 min-w-0" :class="{'active': lensSwitcherExpanded}" 
        @click="toggleLensSwitcher">
          <div class="lens-switcher-content flex items-center gap-1.5">
            <!-- Loading State -->
            <div v-if="(loadingData || getIsLoadingMetrics) && !userLenses.length" class="absolute left-0 top-0 h-full flex items-center gap-2.5 min-w-0"
            :class="getSidebarCollapsed ? 'pl-1.5' : 'pl-2'" style="width: calc(100% - 52px)">
              <div class="w-8 h-8 flex items-center justify-center rounded-md bg-neutral-alpha-25 animate-pulse"/>
              <div class="expanded-content flex-grow flex flex-col" style="gap: 5px">
                <div class="w-auto h-3 rounded bg-neutral-alpha-25 animate-pulse"/>
                <div class="w-24 h-3 rounded bg-neutral-alpha-25 animate-pulse"/>
              </div>
            </div>
            <!-- Current Lens Account Display -->
            <transition-group name="fade-mount" tag="div" class="flex" style="width: calc(100% - 42px)">
              <div v-for="lens in userLenses" v-if="sidebarLensState?.lensId === lens.id" :key="`lens-selector-${lens.id}`" 
              class="flex-grow flex items-center gap-2.5 min-w-0">
                <LensAvatar :avatarSrc="lens.avatar" :lensName="lens.name" :width="32" :height="32" />
                <div class="expanded-content flex-grow flex flex-col min-w-0 text-left">
                  <BaseText type="label" size="sm" class="text-neutral-alpha-800 truncate">
                    {{ lens.name }}
                  </BaseText>
                  <BaseText type="body" size="xs" class="text-neutral-alpha-600">
                    Last updated {{ getLensUpdatedTimeAgo(lens.last_refreshed_at) }} ago
                  </BaseText>
                </div>
              </div>
            </transition-group>
            <ExpandSwitcherIcon :expanded="lensSwitcherExpanded" class="expanded-content ml-auto text-white flex-shrink-0" />
          </div>
        </button>
        <!-- Switcher Dropdown -->
        <transition name="quick-fade">
          <div v-if="lensSwitcherExpanded" class="lens-switcher-dropdown">
            <LensSwitcherDropdown 
              :current-lens-id="sidebarLensState?.lensId" 
              :lenses="userLenses"
              @close="lensSwitcherExpanded = false"
            />
          </div>
        </transition>
      </div>
    </transition>
    <!-- VIEW ROUTE BUTTONS (Home, API, etc.) -->
    <transition-group name="fade-mount">
      <router-link v-for="(route, index) in viewRoutes" :key="`view-button-${route.key}`" :to="route.routeTo" 
      class="view-route-btn group block rounded-md transition-colors duration-100 flex-shrink-0"
      :class="[
        route.active ? 'bg-neutral-alpha-25 hover:bg-neutral-alpha-50' : 'hover:bg-neutral-alpha-50',
        index === 0 ? 'mt-3' : 'mt-1'
      ]">
        <div class="absolute p-1.5 flex items-center gap-2" style="width: 248px;">
          <div class="relative w-5 h-5">
            <component :is="route.icon" v-bind="route.iconSize ? { width: route.iconSize, height: route.iconSize } : {}" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            :class="route.active ? 'text-neutral-alpha-700' : 'text-neutral-alpha-650 group-hover:text-neutral-alpha-700'" />
          </div>
          <BaseText type="body" size="sm" class="label-text" :class="route.active ? 'text-neutral-alpha-800' : 'text-neutral-alpha-650 group-hover:text-neutral-alpha-800'">
            {{ route.label }}
          </BaseText>
          <div v-if="route.isNew" class="new-badge ml-auto">
            <BaseText type="label" size="xs">
              New
            </BaseText>
          </div>
          <div v-else-if="route.badge" class="percent-badge ml-auto">
            <BaseText type="label" size="xs">
              {{ route.badge }}
            </BaseText>
          </div>
        </div>
      </router-link>
    </transition-group>
    <!-- SEARCH BAR / CREATE NEW BUTTON -->
    <div ref="searchContainer" class="search-create-container flex items-center gap-1 flex-shrink-0" 
    :class="{'past-due-blur-overlay': !loadingData && paymentPastDue}">
      <!-- Search Input -->
      <div class="search-outer-container flex-grow flex items-center gap-2 p-1.5 transition-colors duration-100 rounded-lg min-w-0"
      :class="{'bg-neutral-alpha-50': searchInputHovered && !searchInputFocused}"
      @mouseenter="searchInputHovered = true" @mouseleave="searchInputHovered = false"
      @click="handleCollapsedSearchClick">
        <DynamicSearchIcon sidebar :isHovered="searchInputHovered" :isActive="searchInputFocused || expandedViaSearch" class="flex-shrink-0" />
        <input v-model="search" ref="searchInput" class="search-input flex-grow min-w-0 max-w-none" :placeholder="searchPlaceholder" 
        @focus="searchInputFocused = true" @blur="searchInputFocused = false" />
        <!-- Clear Button -->
        <transition>
          <button v-if="searchInputFocused" class="text-neutral-alpha-600 transition-colors duration-100 hover:text-white"
          @mousedown.stop.prevent="search = ''">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M5.16675 5.16663L10.8334 10.8333M10.8334 5.16663L5.16675 10.8333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </transition>
      </div>
      <!-- Create New Button -->
      <button v-if="getCreateEntityDropdownItems().length > 0" data-tour="sidebar-create-new" class="create-new-button flex items-center justify-center rounded-md"
      ref="createNewButton" :class="{ 'active': createEntityDropdownExpanded }"
      @click="toggleCreateEntityDropdown">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 10.25V14M14 14V17.75M14 14H10.25M14 14H17.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <!-- Create Entity Dropdown -->
      <transition :name="getSidebarCollapsed || isCollapseTransitioning ? 'collapsed-create-entity' : 'create-entity'">
        <div v-if="createEntityDropdownExpanded" data-tour="sidebar-create-dropdown" class="create-entity-dropdown" v-on-clickaway="closeCreateEntityDropdown">
          <SidebarItemDropdown :items="getCreateEntityDropdownItems()" @close="closeCreateEntityDropdown" />
        </div>
      </transition>
    </div>
    <!-- FOLDERS -->
    <div class="folders-container flex-grow flex flex-col pt-2 min-h-0 scrollbar-hide relative" :class="{'overflow-y-auto' : !paymentPastDue}">
      <div v-if="!loadingData && paymentPastDue" class="absolute top-12 left-0 w-full h-full z-50">
        <PaymentPastDueCard @close="initalSubscriptionStatus = null" />
      </div>
      <!-- Data Loading State -->
      <div v-if="loadingData" class="w-full h-full flex flex-col items-center justify-center">
        <BaseLoadingSpinnerCircle class="text-neutral-alpha-700" :width="28" :height="28" :duration="1" />
      </div>
      <transition-group name="fade-mount" :class="{'past-due-blur-overlay': !loadingData && paymentPastDue}">
        <!-- Lens Accounts List -->
        <LensAccounts 
          v-if="!loadingData && sidebarLensState?.state === 'lens-main'"
          :isLocked="lockedProductMap.lens"
          key="lens-accounts-list"
          :lenses="userLenses"
          :search="search"
        />
        <!-- Lens Folders -->
        <LensFolders 
          v-for="lens in userLenses"
          v-if="!loadingData && sidebarLensState?.lensId === lens.id"
          :key="`lens-folders-${lens.id}`"
          :current-lens="lens"
          :folders="lensFoldersMap.get(lens.id)"
          :search="search"
          :folder-modal-open.sync="lensFolderModalOpen"
        />
        <!-- Folders (All Non-Lens Products) -->
        <Folders 
          v-if="!loadingData && !sidebarLensState"
          key="folders-list"
          :folder-modal-open.sync="folderModalOpen"
          :search="search"
          @showManageBoardModal="showManageBoardModal = true"
        />
      </transition-group>
    </div>
    <!-- TRIAL COUNTDOWN -->
    <div v-if="((isFreeTier && !getTeam) || (isFreeTier && getTeam && isTeamOwner)) && isTrialing && (days || hours) && !getSidebarCollapsed"
    class="trial-outer-container flex-shrink-0">
      <div class="trial-inner-container">
        <div class="trial-counter w-full flex flex-col p-1 rounded-xl">
          <BaseText type="body" size="sm" class="text-neutral-alpha-650 px-2 pb-2 pt-1">
            <span class="font-medium text-white">{{ days }} days {{ hours }} hours</span> 
            left on your trial. Get in touch with us if you have any questions.
          </BaseText>
          <button class="upgrade-btn w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg"
          @click="openUpgradeDrawer">
            <UpgradeIcon class="text-white" />
            <BaseText type="label" size="sm" class="text-white">
              Upgrade Foreplay
            </BaseText>
          </button>
        </div>
      </div>
    </div>
    <!-- SIDEBAR FOOTER -->
    <div class="sidebar-footer mt-auto flex-shrink-0">
      <button class="avatar" @click="showUserPopover = !showUserPopover">
        <BaseAvatar :avatar="getUserAvatar" size="28" class="flex-shrink-0 rounded-full object-fit" no-border />
      </button>
      <div class="changelog relative ml-auto">
        <button class="group cursor-pointer select-none p-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-200"
        style="z-index: 200;" @click="openFeaturebaseChangelog">
          <ShipIcon class="text-neutral-alpha-650 transition-colors duration-100 group-hover:text-white" />
        </button>
        <div v-if="getUnreadChangelogCount" class="changelog-ping bg-primary-blue-100" />
      </div>
      <!-- User Popover -->
      <transition>
        <div v-if="showUserPopover" class="user-popover-container" v-on-clickaway="() => showUserPopover = false">
          <UserPopover :referral-link="referralLink" @itemClick="handleUserPopoverClick" />
        </div>
      </transition>
    </div>

    <!-- MODALS -->
    <div class="absolute">
      <SearchModal
        @focusSearch="focusSearch()"
      />
    </div>
    <PostPurchaseModal v-if="getShowUpsell && getInitialLoad" />
    <LensGuestInvitationModal v-if="!hideLensGuestInvitationModal && getPendingLensInvitations.length && getInitialLoad" @close="hideLensGuestInvitationModal = true" />
    <TeamInvitationModal v-else-if="!hideTeamInvitationModal && getPendingInvitations.length && getInitialLoad"
    @close="hideTeamInvitationModal = true" />
    <!-- <PaymentPastDueModal v-else-if="getTeam && getTeamSubscriptionState === 'past_due'" /> -->
    <!-- <UserPaymentFailModal v-else-if="paymentPastDue && getInitialLoad && getUserSubscription?.isAdmin" /> -->
    <!-- <TeamMemberPastDueModal v-else-if="paymentPastDue && getInitialLoad && getTeam && !isTeamCreator" /> -->
    <TrialEndedModal v-else-if="(!paymentPastDue && trialExpired) && getInitialLoad && !getSubscriptionDrawerViewMode" />
    <OnboardingModal
      v-else-if="needsOnboarding && getInitialLoad"
      @close="completedOnboarding = true"
    />
    <LensConnectionLostModal 
      v-else-if="hasProviderDisconnected"
      :disconnected-providers="getProvidersConnectionStatus.disconnected"
      @close="showConnectionModal = false"
    />
    <InstagramModal
      v-if="showInstagramModal"
      @close="() => { showInstagramModal = false }"
    />
    <!-- Manage Board Modal -->
    <ManageBoardModal
      v-if="showManageBoardModal"
      :board="{}"
      @close="showManageBoardModal = false"
    />
    <AdBlockWarningModal v-if="userHasAdBlock" @close="userHasAdBlock = false" />

    <!-- Folder Specific Modals -->
    <portal-target name="folder-modals" />

    <portal-target name="name-tooltips" />

    <!-- Hyros email -->
    <input v-if="userHyrosEmail" type="email" class="hidden absolute" id="user-email" :value="userHyrosEmail">
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import { getTimeAgoShorthand } from '../../utils/dateUtils'
import moment from 'moment'
import remixiconUrl from 'remixicon/fonts/remixicon.symbol.svg'
import firebase from '../../api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import AxiosConfig from '../../api/foreplayServer/serverAxiosConfig'
import detectAdBlock from '../../utils/adblock'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import { SUBSCRIPTION_STATES } from '../../utils/stripe/productMeta'

// Components
import Folders from '../sidebar/Folders'
import LensAccounts from '../sidebar/LensAccounts.vue'
import LensFolders from '../sidebar/LensFolders.vue'
import SidebarItemDropdown from '../sidebar/SidebarItemDropdown.vue'
import UserPopover from '../sidebar/UserPopover.vue'
import LensSwitcherDropdown from '../sidebar/LensSwitcherDropdown.vue'
import LensGuestInvitationModal from '../lens/LensGuestInvitationModal.vue'
import PaymentPastDueCard from '../account/subscription/PaymentPastDueCard.vue'

// Modals
import InstagramModal from '../dashboard/Modals/InstagramModal.vue'
import ManageBoardModal from '../boards/ManageBoardModal.vue'
import OnboardingModal from '../../components/account/OnboardingModal.vue'
import PaymentPastDueModal from '../../components/account/subscription/PaymentPastDueModal.vue'
import PostPurchaseModal from '../account/subcriptionv2/PostPurchaseModal.vue'
import ProductHuntModal from '../account/ProductHuntModal.vue'
import SearchModal from '../search/SearchModal'
import TeamInvitationModal from '../../components/account/teams/TeamInvitationModal.vue'
import TeamMemberPastDueModal from '../account/subscription/TeamMemberPastDueModal.vue'
import TrialEndedModal from '../../components/account/subscription/TrialEndedModal.vue'
import UserPaymentFailModal from '../account/subscription/UserPaymentFailModal.vue'
import LensConnectionLostModal from '../lens/LensConnectionLostModal.vue'
import { isDemoLensRoute } from '@/utils/lens/demo/lensDemoConstants'
import AdBlockWarningModal from '../account/AdBlockWarningModal.vue'

// Icons
import ForeplayLogoSidebarIcon from './Icons/SidebarIcons/ForeplayLogoSidebarIcon.vue'
import AnimatedProductNavIcon from './Icons/SidebarIcons/AnimatedProductNavIcon.vue'
import CollapseIcon from './Icons/SidebarIcons/CollapseIcon.vue'
import HomeIcon from './Icons/SidebarIcons/HomeIcon.vue'
import MCPIcon from './Icons/ApiIcons/MCPIcon.vue'
import DynamicSearchIcon from './Icons/SearchIcons/DynamicSearchIcon.vue'
import ShipIcon from './Icons/SidebarIcons/ShipIcon.vue'
import CreateFolderItemIcon from './Icons/SidebarIcons/dropdowns/CreateFolderItemIcon.vue'
import CreateBoardItemIcon from './Icons/SidebarIcons/dropdowns/CreateBoardItemIcon.vue'
import CreatePerformanceReportItemIcon from './Icons/SidebarIcons/dropdowns/CreatePerformanceReportItemIcon.vue'
import LensIcon from './Icons/SidebarIcons/LensIcon.vue'
import ExpandSwitcherIcon from './Icons/SidebarIcons/ExpandSwitcherIcon.vue'
import LensAvatar from '../sidebar/LensAvatar.vue'
import LockedIcon from "./Icons/LockedIcon.vue"
import UpgradeIcon from './Icons/SidebarIcons/UpgradeIcon.vue'
import BountiesIcon from './Icons/BountiesIcon.vue'
import RocketIcon from './Icons/RocketIcon.vue'

const UNLABELED_ROUTES = ['home', 'api', 'bounties', 'setup']

export default {
  name: 'Sidebar',
  mixins: [clickaway],
  components: {
    Folders,
    LensAccounts,
    LensFolders,
    SidebarItemDropdown,
    UserPopover,
    LensSwitcherDropdown,
    LensAvatar,
    PaymentPastDueCard,

    // Modals
    InstagramModal,
    ManageBoardModal,
    OnboardingModal,
    PaymentPastDueModal,
    PostPurchaseModal,
    ProductHuntModal,
    SearchModal,
    TeamInvitationModal,
    TeamMemberPastDueModal,
    TrialEndedModal,
    UserPaymentFailModal,
    AdBlockWarningModal,
    LensGuestInvitationModal,
    LensConnectionLostModal,

    // Icons
    ForeplayLogoSidebarIcon,
    AnimatedProductNavIcon,
    CollapseIcon,
    HomeIcon,
    LockedIcon,
    DynamicSearchIcon,
    ShipIcon,
    LensIcon,
    ExpandSwitcherIcon,
    UpgradeIcon,
    BountiesIcon,
  },
  data () {
    const productLabelMap = {
      library: {
        name: 'Swipe File',
        hotkey: '⌘L',
        description: 'Save & share creative inspiration.'
      },
      discovery: {
        name: 'Discovery',
        hotkey: '⌘D',
        description: 'Search the community ad database.'
      },
      briefs: {
        name: 'Briefs',
        hotkey: '⌘B',
        description: 'Turn inspiration into actionable briefs.'
      },
      spyder: {
        name: 'Spyder',
        hotkey: '⌘I',
        description: 'Track and analyze competitor advertising.'
      },
      lens: {
        name: 'Lens',
        hotkey: '⌘X',
        description: 'Advertising analytics for creative teams.'
      }
    }

    return {      
      betaIds: [],
      remixiconUrl,
      completedOnboarding: true,
      unseenUpdates: false,
      editBoard: {},
      referralLink: '',
      spyderLimit: 0,

      // Search States
      search: '',
      searchInputHovered: false,
      searchInputFocused: false,

      // Free Trial States
      trialEnd: moment().format('X'),
      days: 0,
      hours: 0,

      // Route Button Meta
      productRoutes: [
        { key: 'library', routeName: 'LibraryView' },
        { key: 'discovery', routeName: 'DiscoveryView' },
        { key: 'briefs', routeName: 'BriefsView' },
        { key: 'spyder', routeName: 'SpyderBrandsView' },
        { key: 'lens', routeName: 'LensView' }
      ],

      // Animated Product Label States
      productLabelMap,
      productKeys: Object.keys(productLabelMap),
      hoveredProduct: null,
      lastHoveredProduct: null,
      labeledProduct: null,
      productSwitcherHovered: false,
      doAnimLabel: true,

      // UI States
      loadingData: true,
      createEntityDropdownExpanded: false,
      // The board-share walkthrough holds the create dropdown open for its sidebar step
      tourHoldingCreateOpen: false,
      showUserPopover: false,
      lensSwitcherExpanded: false,
      preloadedLensAvatars: [],
      showConnectionModal: true,
      userHasAdBlock: false,
      initalSubscriptionStatus: null,

      // Collapsible Sidebar States
      isCollapseTransitioning: false,
      resizeObserver: null,
      reflowRaf: 0,
      expandedViaSearch: false,

      // Modal States
      showManageBoardModal: false,
      closeProductHuntModal: false,
      folderModalOpen: false,
      lensFolderModalOpen: false,
      showInstagramModal: false,
      hideTeamInvitationModal: false,
      hideLensGuestInvitationModal: false,
    }
  },
  async created () {
    this.spyderLimit = this.getSpyderLimit
    this.lensLimit = this.getLensLimit

    // Check local storage for sidebar collapsed state
    const sidebarCollapsed = localStorage.getItem('sidebarCollapsed')
    if (sidebarCollapsed) {
      this.SET_SIDEBAR_COLLAPSED(sidebarCollapsed === 'true')
    }

    if (!this.currentRouteGroup || this.currentRouteGroup === 'home') this.doAnimLabel = false
    this.SET_SPYDER_SUBSCRIPTIONS_LOADING(true)

    await Promise.all([
      this.fetchBoards(),
      this.fetchFolders().catch(err => { console.error('Error fetching folders', err) }),
      this.fetchSidebarBriefs().catch(err => { console.error('Error fetching sidebar briefs', err) }),
      this.fetchSpyderSubscriptions().catch(err => { console.error('Error fetching spyder subscriptions', err) }),
      this.fetchSidebarLenses().catch(err => { console.error('Error fetching sidebar lenses', err) }),
      this.fetchFreeTrialDate().catch(err => { console.error('Error fetching free trial date', err) }),
    ])

    if (this.getUser.starredBoards) {
      this.SET_STARRED_BOARDS(
        this.getBoards.filter((board) =>
          this.getUser.starredBoards?.includes(board.id)
        )
      )
    }

    // Compute Trial End Date
    if (this.isFreeTier && Date.now() < this.getUserFreeTrialDate + 6.048e8) {
      this.trialEnd = moment(this.getUserFreeTrialDate).add(7, 'days')
    }

    // Pre-fetch lens account avatars
    this.getSidebarLenses.forEach(({ avatar }) => {
      if (!avatar || !avatar?.length) return
      const img = new Image()
      img.src = avatar
      img.onload = () => { this.preloadedLensAvatars.push(img) } // Prevent garbage collection
    })

    this.SET_SPYDER_SUBSCRIPTIONS_LOADING(false)
    this.loadingData = false

    this.fetchOrCreateReferral()

    // Prompt adblock modal only if not acknowledged
    if (localStorage.getItem('adblock_acknowledged') === 'true') {
      this.userHasAdBlock = false
    } else {
      this.userHasAdBlock = await detectAdBlock()
    }
  },
  async mounted () {
    console.log(`%c Sidebar mounted ${performance.now() - window.t0}ms`, 'color: #00ff00; font-weight: bold;')

    EventBus.$on('tour:step-enter', this.handleTourStepEnter)
    EventBus.$on('tour:step-leave', this.handleTourStepLeave)

    // Configure resize observer to fire reflow events when the sidebar is collapsed (for AdvertisementList)
    // this.$nextTick(() => {
    //   if (window.ResizeObserver && this.$refs.sidebarRoot) {
    //     this.resizeObserver = new ResizeObserver(() => {
    //       cancelAnimationFrame(this.reflowRaf)
    //       this.reflowRaf = requestAnimationFrame(() => {
    //         window.dispatchEvent(new Event('resize'))
    //       })
    //     })
    //     this.resizeObserver.observe(this.$refs.sidebarRoot)
    //   }
    // })
    
    // (Fallback) Check onboarding and subscription status - Wait 500ms for vuex state to initialize
    await new Promise(resolve => setTimeout(resolve, 500))
    const onboardingCompleted = this.getUserOnboardingStatus === 'FINISH'
    if (!onboardingCompleted && !this.guestStatus?.isGuestOnly) {
      this.$router.push({ name: 'UserOnboardView' })
    }
    this.initalSubscriptionStatus = this.getUserSubscription?.status
  },
  beforeDestroy () {
    EventBus.$off('tour:step-enter', this.handleTourStepEnter)
    EventBus.$off('tour:step-leave', this.handleTourStepLeave)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    cancelAnimationFrame(this.reflowRaf)
  },
  watch: {
    currentRouteGroup: {
      handler (newVal) {
          if (UNLABELED_ROUTES.includes(newVal)) this.labeledProduct = null
        else this.labeledProduct = newVal
      },
      immediate: true
    },
    sidebarLensState (oldVal, newVal) {
      console.log(oldVal?.state, newVal?.state)
      if (oldVal?.state !== newVal?.state) {
        this.search = ''
        this.lensSwitcherExpanded = false
        if (this.$refs.searchContainer) {
          this.$refs.searchContainer.classList.add('fade-in')
          this.$refs.searchContainer.addEventListener('animationend', () => {
            this.$refs.searchContainer.classList.remove('fade-in')
          })
        }
      }
    },
    trialEnd () {
      this.computeTrialCounter()
    },
    // Launchpad progress powers the nav badge. Watched, not read once in
    // created: after a fresh login the sidebar mounts before the subscription
    // loads. Not awaited.
    isTrialing: {
      immediate: true,
      handler (isTrialing) {
        if (isTrialing && !this.isLaunchpadComplete) this.fetchSetupProgress()
      }
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getBoards']),
    ...mapGetters('BriefsModule', ['getIsInspoMode']),
    ...mapGetters('AuthModule', [
      'getInitialLoad',
      'getPendingLensInvitations',
      'getPendingInvitations',
      'getShowUpsell',
      'getSurveyViewed',
      'getTeam',
      'getTeamSubscriptionState',
      'getUser',
      'getUserAvatar',
      'getUserEmail',
      'getUserFreeTrialDate',
      'getUserName',
      'getUserSubscription',
      'getTeamSubscription',
      'isFreeTier',
      'isTeamOwner',
      'getSpyderLimit',
      'getLensLimit',
      'getIsLegacy',
      'isBetaUser',
      'getGuestStatus',
      'getHasApiAccess',
      'getUserOnboardingStatus'
    ]),
    ...mapGetters('MiscModule', ['getSidebarCollapsed', 'getSidebarCollapseDuration', 'getTheme', 'getUnreadChangelogCount', 'getSubscriptionDrawerViewMode']),
    ...mapGetters('LensModule', ['getSidebarLenses', 'getProvidersConnectionStatus', 'getLenses', 'getSidebarLenses']),
    ...mapGetters('MetricsModule', ['getIsLoadingMetrics']),
    ...mapGetters('SetupModule', ['getSetupPercent', 'getSetupProgressLoaded']),
    ...mapGetters('TourModule', ['getActiveTour']),
    viewRoutes () {
      // Route buttons that render under the product selector
      // Launchpad (setup checklist) is only for trialing users, until it's complete
      const setupRoutes =  !this.isTrialing || this.isLaunchpadComplete ? [] : [{
        key: 'setup',
        label: 'Launchpad',
        routeTo: { name: 'SetupView' },
        icon: RocketIcon,
        // The rocket fills its whole viewBox, so it reads heavy at 20px
        iconSize: 16,
        active: this.currentRouteGroup === 'setup',
        // Hidden until progress loads so it never flashes 0%
        badge: this.getSetupProgressLoaded && this.getSetupPercent < 100 ? `${this.getSetupPercent}%` : null,
        topMargin: '4px'
      }]
      if (this.sidebarLensState) {
        // Lens mode view routes
        return this.sidebarLensState.lensId ? [
          {
            key: 'dashboard',
            label: 'Dashboard',
            routeTo: { name: 'LensDashboardView', params: { lensId: this.sidebarLensState.lensId } },
            icon: LensIcon,
            active: this.isLensDashboardRoute,
            topMargin: '4px'
          },
          ...setupRoutes
        ] : setupRoutes
      } else {
        // Non-lens mode view routes
        return [
          { 
            key: 'home', 
            label: 'Home', 
            routeTo: { name: 'DashboardView' }, 
            icon: HomeIcon,
            active: this.currentRouteGroup === 'home',
            topMargin: '12px'
          },
          ...setupRoutes,
          { 
            key: 'api', 
            label: 'API & MCP', 
            routeTo: { name: 'ApiView' }, 
            icon: MCPIcon,
            active: this.currentRouteGroup === 'api',
            topMargin: '4px'
          },
          // { key: 'bounties', label: 'Bounties', routeName: 'BountiesView', icon: BountiesIcon }
        ]
      }
    },
    productHoverTimeout () {
      return this.getSidebarCollapsed ? 500 : 100
    },
    userHyrosEmail () {
      return this.$route?.query?.email || this.getUser?.email
    },
    maxLensUsed () {
      const totalLenses = this.getSidebarLenses ? this.getSidebarLenses?.length : 0
      return totalLenses >= this.getLensLimit
    },
    currentRouteGroup () {
      if (!this.$route) return null
      if (this.$route.name === 'DashboardView') return 'home'
      return this.$route?.meta?.product ?? null
    },
    // ---- Sidebar Folders State Controls ----
    sidebarLensState () {
      if (this.$route.name === 'LensView') return { state: 'lens-main', lensId: null }
      if (this.$route.params.lensId) return { state: 'lens', lensId: this.$route.params.lensId }
      return null
    },
    userLenses () {
      if (!this.getSidebarLenses) return []
      return this.getSidebarLenses.map(({ folders, ...rest }) => ({ ...rest }))
    },
    lensFoldersMap () {
      if (!this.getSidebarLenses) return null
      const foldersMap = new Map()
      this.getSidebarLenses.forEach(({ id, folders }) => {
        foldersMap.set(id, folders)
      })
      return foldersMap
    },
    // ----------------------------------------
    showProductHuntModal () {
      return !this.getUser.viewedProductHuntModal && !this.closeProductHuntModal
    },
    isTeamCreator () {
      return this.getTeam && this.getTeam.created_by === this.getUser.user_id
    },
    isLaunchpadComplete () {
      return this.getUser?.setupPercentSynced >= 100
    },
    isTrialing () {
      const subscription = this.getUserSubscription
      const noSub = !subscription?.status || subscription?.status === SUBSCRIPTION_STATES.NOT_ACTIVE
      return noSub ? false : subscription?.status === SUBSCRIPTION_STATES.TRIALING
    },
    paymentPastDue () {
      return this.getUserSubscription?.status === SUBSCRIPTION_STATES.PAST_DUE && this.initalSubscriptionStatus === SUBSCRIPTION_STATES.PAST_DUE
    },
    trialExpired () {
      const pendingInvitations = this.getPendingInvitations && this.getPendingInvitations?.length > 0
      const inactiveOrPastDue = this.getUserSubscription?.status !== SUBSCRIPTION_STATES.ACTIVE && this.getUserSubscription?.status !== SUBSCRIPTION_STATES.PAST_DUE
      const noSub = (!this.getUserSubscription?.status || inactiveOrPastDue) && !this.getHasApiAccess
      const isTrialing = this.isTrialing
      const isGuestOnly = this.getGuestStatus.isGuestOnly
      if (isTrialing || isGuestOnly || pendingInvitations) return false

      return noSub
    },
    hasProviderDisconnected () {
      const subscription = this.getUserSubscription
      const noLens = (!subscription.addOns?.lens && this.lensLimit === 0) || (this.getSidebarLenses.length === 0)
      if (noLens) return false
      // Demo Lens pages run on sample data; real connection problems don't apply there
      const isLens = this.$route?.meta?.product === 'lens' && !isDemoLensRoute(this.$route.params)

      const { disconnected } = this.getProvidersConnectionStatus
      const userOwnedDisconnections = disconnected.filter(provider => provider?.user_id === this.getUser?.user_id)
      return userOwnedDisconnections.length && this.showConnectionModal && isLens && this.getInitialLoad
    },
    shouldSeeSurvey () {
      return !this.isFreeTier && !this.getSurveyViewed
    },
    canSeeBriefs () {
      return this.getUserSubscription?.product?.name === 'Full Workflow' || !this.getUserSubscription?.product?.name
    },
    needsOnboarding () {
      return !this.completedOnboarding && this.getUser.trialStarted > 1663193252602 && !this.getUser.onboardingComplete
    },
    lockedProductMap () {
      const subscription = this.getUserSubscription

      const isBasic = subscription.internalProductId === 'basic_plan'
      const isLegacy = this.getIsLegacy

      const lensLimit = this.getLensLimit || 0
      const spyderLimit = this.getSpyderLimit || 0
      const { isGuestOnly, isGuest } = this.getGuestStatus || {}
      const guestBypass = isGuestOnly ? false : isGuest

      const noSub = subscription?.status === SUBSCRIPTION_STATES.NOT_ACTIVE || !subscription?.status

      const noSubNoLimit = !subscription?.addOns?.lens && lensLimit === 0
      if (noSub) {
        return {
          briefs: true,
          discovery: true,
          spyder: true,
          lens: !isGuestOnly,
          library: true,
          api: !this.getHasApiAccess
        }
      }
      return {
        briefs: isGuestOnly || this.paymentPastDue,
        discovery: isGuestOnly,
        spyder: ((isBasic && spyderLimit === 0) || (isLegacy && spyderLimit === 0) || spyderLimit === 0) || this.paymentPastDue,
        lens: guestBypass ? false : noSubNoLimit || this.paymentPastDue,
        library: isGuestOnly || this.paymentPastDue,
        api: !this.getHasApiAccess
      }
    },
    // ====== UI COMPUTED VALUES ======
    productKeyShortcuts () {
      return this.productKeys.filter((product) => !this.lockedProductMap[product])
    },
    productLabelRenderData () {
      const defaults = {
        nameWidth: '121px',
        hotkeyWidth: '34px',
        offsetDistance: 0
      }

      const isLibraryLocked = this.lockedProductMap.library
      const isDiscoveryLocked = this.lockedProductMap.discovery
      const isBriefsLocked = this.lockedProductMap.briefs
      const isSpyderLocked = this.lockedProductMap.spyder
      const isLensLocked = this.lockedProductMap.lens

      const productConfig = {
        library: {
          nameWidth: isLibraryLocked ? '132px' : '118px',
          hotkeyWidth: '34px',
          offsetDistance: 0
        },
        discovery: {
          nameWidth: isDiscoveryLocked ? '133px' : '121px',
          hotkeyWidth: '36px',
          offsetDistance: 14
        },
        briefs: {
          nameWidth: isBriefsLocked ? '102px' : '92px',
          hotkeyWidth: '36px',
          offsetDistance: 62
        },
        spyder: {
          nameWidth: isSpyderLocked ? '113px' : '96px',
          hotkeyWidth: '30px',
          offsetDistance: 114
        },
        lens: {
          nameWidth: isLensLocked ? '98px' : '86px',
          hotkeyWidth: '36px',
          offsetDistance: isLensLocked ? 137 : 144
        },
      };

      return productConfig?.[this.labeledProduct] || defaults;
    },
    productNameWidth () {
      return this.productLabelRenderData.nameWidth
    },
    productHotkeyWidth () {
      return this.productLabelRenderData.hotkeyWidth
    },
    collapsedProductLabelMeta () {
      // Width of description text + 28px
      const widths = new Map([['library', '247px'], ['discovery', '266px'], ['briefs', '277px'], ['spyder', '305px'], ['lens', '293px']])
      // 48*index
      const yOffsets = new Map([['library', 0], ['discovery', 48], ['briefs', 96], ['spyder', 144], ['lens', 192]])
      return {
        width: this.productSwitcherHovered ? widths.get(this.hoveredProduct) ?? widths.get(this.lastHoveredProduct) ?? '0px' : '0px',
        yOffset: yOffsets.get(this.lastHoveredProduct)
      }
    },
    labelOffsetDistance () {
      return this.productLabelRenderData.offsetDistance
    },
    searchPlaceholder () {
      if (this.sidebarLensState?.state === 'lens-main') return 'Search accounts...'
      if (this.sidebarLensState?.lensId) return 'Search reports...'
      return 'Search...'
    },
    isLensDashboardRoute () {
      return this.$route?.meta?.product === 'lens' && this.$route?.meta?.tab?.length
    }
  },
  methods: {
    ...mapActions('AuthModule', ['logoutUser', 'fetchFreeTrialDate', 'checkTeamExpired', 'debugResetStore']),
    ...mapActions('BoardsModule', ['fetchBoards', 'fetchFolders']),
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions', 'fetchSpyderLimit']),
    ...mapActions('BriefsModule', ['fetchSidebarBriefs']),
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    ...mapActions('SetupModule', ['fetchSetupProgress']),
    ...mapMutations('AuthModule', ['SET_USER', 'SET_STARRED_BOARDS']),
    ...mapMutations('MiscModule', ['SET_SIDEBAR_COLLAPSED', 'SET_SETTINGS_DRAWER_VIEW_MODE', 'SET_SUBSCRIPTION_DRAWER_VIEW_MODE']),
    ...mapMutations('SpyderModule', ['SET_SPYDER_SUBSCRIPTIONS_LOADING']),
    ...mapMutations('LensModule', ['SET_SHOW_LENS_CREATION_MODAL', 'SET_SHOW_REPORT_CREATION_MODAL']),
    handleLogout () {
      console.log('logout')
      this.logoutUser()
    },
    sendToManagePlan () {
      this.$router.push({ name: 'PaymentView' })
    },
    async getBetaUsers () {
      this.betaIds = await FirebaseAPI.Beta.getBetaUserIds()
    },
    async fetchOrCreateReferral () {
      try {
        const { Aa: token } = firebase.auth().currentUser
        const response = await AxiosConfig.post('/campaigns/referral', null, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        this.referralLink = response.data.url
      } catch (error) {
        console.log('referral error', error)
      }
    },
    handleDropdownClick (itemName) {
      if (itemName === 'Logout') {
        this.handleLogout()
      } else if (itemName === 'Settings' || itemName === 'Profile') {
        this.SET_SETTINGS_DRAWER_VIEW_MODE('account')
      } else if (itemName === 'Help') {
        this.showIntercom(itemName)
      } else if (itemName === 'Feature Releases') {
        window.open('https://foreplay.co/ships', '_blank')
      } else if (itemName === 'Perks & Discounts') {
        this.$router.push({ name: 'PerksView' })
      } else if (itemName === 'Become an Affiliate') {
        window.open('https://www.foreplay.co/affiliates', '_blank')
      }
    },
    handleUserPopoverClick (itemName) {
      switch (itemName) {
        case 'settings': this.SET_SETTINGS_DRAWER_VIEW_MODE('account'); break;
        case 'perks': this.$router.push({ name: 'PerksView' }); break;
        case 'affiliate': window.open('https://www.foreplay.co/affiliates', '_blank'); break;
        case 'clients': break;
        case 'marketers': break;
        case 'support': window.Intercom('show'); break;
        case 'knowledge': window.open('https://foreplay.featurebase.app/en/help', '_blank'); break;
        case 'logout': this.handleLogout(); break;
      }
      this.showUserPopover = false
    },
    createLensCB () {
      if (this.maxLensUsed) {
        this.$showAlert({ type: 'info', message: 'Max lenses created' })
        return
      }
      this.SET_SHOW_LENS_CREATION_MODAL(true)
    },
    getCreateEntityDropdownItems () {
      if (this.sidebarLensState?.state === 'lens-main') {
        return this.lockedProductMap?.lens 
          ? [] 
          : [{ name: 'Create Lens', action: this.createLensCB, icon: LensIcon, locked: this.getGuestStatus?.isGuestOnly }]
      } else if (this.sidebarLensState?.state === 'lens') {
        return this.lockedProductMap?.lens ? [] : [
          { name: 'Create Folder', action: () => { this.lensFolderModalOpen = true }, icon: CreateFolderItemIcon },
          { 
            name: 'Create Report', 
            action: () => { 
              const onLensDashboardRoute = this.$route?.meta?.product === 'lens' && this.$route?.meta?.tab?.length
              if (!onLensDashboardRoute) {
                this.$router.push({ name: 'LensReports', params: { lensId: this.sidebarLensState?.lensId } }).catch(err => {
                  console.error('Error navigating to LensReports:', err)
                })
              }
              this.SET_SHOW_REPORT_CREATION_MODAL(this.sidebarLensState.lensId) 
            }, 
            icon: CreatePerformanceReportItemIcon 
          }
        ]
      }
      return [
        { name: 'Create Folder', action: () => { this.folderModalOpen = true }, icon: CreateFolderItemIcon },
        { name: 'Create Board', action: this.openCreateBoard, icon: CreateBoardItemIcon }
      ]
    },
    computeTrialCounter () {
      const duration = moment.duration(this.trialEnd.diff(moment(), 'seconds'), 'seconds')
      this.days = duration.days() > 0 ? duration.days() : 0
      this.hours = duration.hours() > 0 ? duration.hours() : 0
    },
    openUpgradeDrawer () {
      this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({state: true})
    },
    openFeaturebaseChangelog () {
      window.Featurebase('manually_open_changelog_popup')
    },

    // ==============================================================================
    // ================================= UI METHODS =================================
    // ==============================================================================

    toggleSidebarCollapse () {
      if (this.isCollapseTransitioning) return
      this.isCollapseTransitioning = true

      // Hide dropdowns
      this.createEntityDropdownExpanded = false
      this.lensSwitcherExpanded = false

      // Set the global sidebar collapsed state
      const newCollapsedState = !this.getSidebarCollapsed
      this.SET_SIDEBAR_COLLAPSED(newCollapsedState)
      localStorage.setItem('sidebarCollapsed', newCollapsedState)
      this.$refs.createNewButton?.classList?.add('anim-collapse')

      setTimeout(() => { 
        this.isCollapseTransitioning = false 
        this.$refs.createNewButton?.classList?.remove('anim-collapse')
        EventBus.$emit('sidebar:collapse-transition-end')
        setTimeout(() => { window.dispatchEvent(new Event('resize')) }, 1)
      }, this.getSidebarCollapseDuration)
    },
    handleCollapsedSearchClick () {
      if (!this.getSidebarCollapsed) return
      this.expandedViaSearch = true
      this.toggleSidebarCollapse()
      setTimeout(() => { 
        this.$refs?.searchInput?.focus()
        this.expandedViaSearch = false 
      }, Math.floor(this.getSidebarCollapseDuration * 0.25))
    },
    toggleCreateEntityDropdown () {
      if (this.getSidebarCollapsed) this.toggleSidebarCollapse()
      this.createEntityDropdownExpanded = !this.createEntityDropdownExpanded
    },
    closeCreateEntityDropdown () {
      if (!this.tourHoldingCreateOpen) this.createEntityDropdownExpanded = false
    },
    openCreateBoard () {
      // The board-share walkthrough owns the Launchpad's create-board modal;
      // don't open a second copy from the sidebar
      if (this.getActiveTour?.id === 'board-share') {
        this.tourHoldingCreateOpen = false
        this.createEntityDropdownExpanded = false
        EventBus.$emit('tour:open-manage-board')
        return
      }
      this.showManageBoardModal = true
    },
    handleTourStepEnter ({ openSidebarCreate } = {}) {
      if (!openSidebarCreate) return
      this.tourHoldingCreateOpen = true
      if (this.getSidebarCollapsed) this.toggleSidebarCollapse()
      this.createEntityDropdownExpanded = true
    },
    handleTourStepLeave () {
      if (!this.tourHoldingCreateOpen) return
      this.tourHoldingCreateOpen = false
      this.createEntityDropdownExpanded = false
    },
    toggleLensSwitcher () {
      if (this.getSidebarCollapsed) this.toggleSidebarCollapse()
      this.lensSwitcherExpanded = !this.lensSwitcherExpanded
    },
    getLensUpdatedTimeAgo (dateStr) {
      if (!dateStr) return '--'
      const date = new Date(dateStr)
      return getTimeAgoShorthand(date)
    },
    focusSearch () {
      // Find the main search bar input if it exists, otherwise focus sidebar search
      const searchInput = document.querySelector('.search .search-input-container .search-input')
      if (searchInput) {
        searchInput.focus()
      } else if (this.getSidebarCollapsed) {
        this.handleCollapsedSearchClick()
      } else {
        this.$refs.searchInput.focus()
      }
    },
    showIntercom (itemName) {
      if (itemName === 'Help') {
        window.Intercom('show')
      }
    },
    folderSearch ($event) {
      this.search = $event.target.value
    },
    handleProductSwitcherHover () {
      this.productSwitcherHovered = true
      setTimeout(() => {
        this.doAnimLabel = true
      }, 10)
    },
    handleProductSwitcherUnhover () {
      this.productSwitcherHovered = false
      this.lastHoveredProduct = null
      if (!this.currentRouteGroup) this.doAnimLabel = false
      if (!UNLABELED_ROUTES.includes(this.currentRouteGroup)) this.labeledProduct = this.currentRouteGroup
      else this.labeledProduct = null
    }
  }
}
</script>

<style scoped>
/* ======================== SIDEBAR ROOT & Gradient Colors/Animations ======================== */
@property --gradient-color {
  syntax: '<color>';
  inherits: false;
  initial-value: rgba(0, 0, 0, 0);
}
.sidebar-root {
  background: radial-gradient(100% 50% at 100% 50%, var(--gradient-color) 0%, rgba(15, 16, 25, 0.20) 100%);
  transition: 
    width var(--collapse-transition-duration) ease-in-out, 
    padding var(--collapse-transition-duration) ease-in-out,
    --gradient-color 750ms ease-in-out;
  padding: 20px 20px;
  width: 288px;
}
.sidebar-root.collapsed {
  padding: 20px 16px;
  width: 76px;
}
.library-gradient { --gradient-color: rgba(0, 107, 255, 0.16); }
.discovery-gradient { --gradient-color: rgba(109, 49, 232, 0.16); }
.spyder-gradient { --gradient-color: rgba(255, 84, 83, 0.16); }
.briefs-gradient { --gradient-color: rgba(31, 213, 163, 0.16); }
.lens-gradient { --gradient-color: rgba(255, 255, 255, 0.16); }

/* ======================== SIDEBAR HEADER (Logo & Collapse Button) ======================== */
.sidebar-header {
  position: relative;
  width: 100%;
  height: 32px;
  transition: height var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-header {
  height: 68px;
}
.sidebar-header .logo {
  position: absolute;
  width: 100%;
  min-width: 108px;
  height: 32px;
  top: 0px;
  left: 0px;
  transform: translateX(0px);
  transition: transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-header .logo {
  transform: translateX(6px);
}
.sidebar-header .collapse-btn {
  position: absolute;
  top: 2px;
  right: 0px;
  z-index: 20;
  transform: translate(0px, 0px);
  transition: transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-header .collapse-btn {
  transform: translate(-8px, 38px);
}
.guest-tag {
  position: absolute;
  top: 5px;
  left: 112px;
  color: rgba(255, 255, 255, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.68);
  padding: 2px 4px;
  border-radius: 4px;
  text-transform: uppercase;
}

/* ============================== PRODUCT SELECTOR ============================== */
.product-selector {
  min-height: 48px;
  transition: min-height var(--collapse-transition-duration) ease-in-out;
}
.collapsed .product-selector {
  min-height: 236px;
}
.product-button {
  position: relative;
  width: 48px;
  height: 48px;
  padding: 4px;
  border-radius: 10px;
  transform: translate(0px, 0px);
  transition: 
    background-color 150ms ease-in-out,
    width var(--collapse-transition-duration) ease-in-out,
    height var(--collapse-transition-duration) ease-in-out,
    padding var(--collapse-transition-duration) ease-in-out,
    transform var(--collapse-transition-duration) ease-in-out;
}
.product-button:hover:not(.collapsed), .product-button.show-active:not(.collapsed) {
  background-color: rgba(255, 255, 255, 0.12); /* Neutral/Alpha/200 */
}
.collapsed .product-button {
  background-color: rgba(255, 255, 255, 0.06); /* Neutral/Alpha/25 */
  width: 44px;
  height: 44px;
  padding: 6px;
}
.collapsed .product-button:hover, .collapsed .product-button.show-active {
  background-color: rgba(255, 255, 255, 0.20);
}
/* Collapsed Position States */
.collapsed .product-button:nth-child(2) { transform: translate(-46px, 48px); }
.collapsed .product-button:nth-child(3) { transform: translate(-92px, 96px); }
.collapsed .product-button:nth-child(4) { transform: translate(-138px, 144px); }
.collapsed .product-button:nth-child(5) { transform: translate(-184px, 192px); }

/* ============================ Product Name/Hotkey Label ============================ */
.product-label-container {
  position: absolute;
  bottom: 0;
  left: 0;
  min-width: 121px;
  max-width: 149px;
  height: 28px;
  display: flex;
  justify-content: center;
  transform-origin: center;
  transition: transform 150ms ease-in-out;
}
.product-label {
  position: relative;
  transition: width 150ms ease-in-out;
}
.product-text {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
}
.product-hotkey {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  transition: width 150ms linear;
  background-color: #0F1019;
  opacity: 0.68;
  display: flex;
  justify-content: center;
}
.product-label-enter-active {
  animation: productLabelEnter 250ms ease-in-out forwards;
}
.product-label-leave-active {
  animation: productLabelLeave 150ms ease-in-out forwards;
}
@keyframes productLabelEnter {
  0% { opacity: 0; height: 0px; }
  50% { opacity: 0; height: 32px; }
  100% { opacity: 1; height: 32px; }
}
@keyframes productLabelLeave {
  0% { opacity: 1; height: 32px; }
  50% { opacity: 0; height: 32px; }
  100% { opacity: 0; height: 0px; }
}

.product-label-swap-enter-active, .product-label-swap-leave-active, .product-hotkey-swap-enter-active {
  transition: opacity 150ms ease-in-out;
}
.product-hotkey-swap-leave-active {
  transition: opacity 150ms ease-in-out;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.product-label-swap-enter-from, .product-label-swap-enter, .product-label-swap-leave-to,
.product-hotkey-swap-enter-from, .product-hotkey-swap-enter, .product-hotkey-swap-leave-to {
  opacity: 0;
}
.product-label-swap-enter-to, .product-label-swap-leave-from,
.product-hotkey-swap-enter-to, .product-hotkey-swap-leave-from {
  opacity: 1;
}
.disable-transitions, .disable-transitions * {
  transition: none !important;
}

/* COLLAPSED Product Label Animations */
.collapsed-product-label {
  position: fixed;
  left: 68px;
  top: 90px;
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.17) 0%, rgba(255, 255, 255, 0.16) 100%), #060710;
  pointer-events: none;
  transition:
    opacity 150ms ease-in-out,
    width 150ms ease-in-out,
    transform 150ms ease-in-out;
  transform: translateY(0px);
  z-index: 500000;
  overflow: hidden;
}
.collapsed-label-text {
  position: absolute;
  top: 6px;
  left: 8px;
  width: max-content;
  height: 100%;
  white-space: nowrap;
  flex-wrap: nowrap;
}
.collapsed-product-label-enter-active {
  transition:
    opacity 200ms ease-in-out,
    width 200ms ease-out;
}
.collapsed-label-text-swap-enter-active, .collapsed-label-text-swap-leave-active {
  transition: opacity 150ms ease-in-out;
}
.collapsed-product-label-enter-from, .collapsed-product-label-enter, .collapsed-product-label-leave-to,
.collapsed-label-text-swap-enter-from, .collapsed-label-text-swap-enter, .collapsed-label-text-swap-leave-to {
  opacity: 0;
}
.collapsed-product-label-enter-to, .collapsed-product-label-leave-from,
.collapsed-label-text-swap-enter-to, .collapsed-label-text-swap-leave-from {
  opacity: 1;
}

/* ========================= LENS SWITCHER DROPDOWN CLASSES ========================== */
.lens-switcher-button {
  position: relative;
  width: 100%;
  height: 48px;
  overflow: hidden;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.06);
  transition:
    width var(--collapse-transition-duration) ease-in-out,
    height var(--collapse-transition-duration) ease-in-out,
    background-color 100ms ease-in-out;
}
.collapsed .lens-switcher-button {
  width: 44px;
  height: 44px;
  background-color: transparent;
}
.lens-switcher-button.active, .lens-switcher-button:hover {
  background-color: rgba(255, 255, 255, 0.08);
}
.lens-switcher-content {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 48px;
  width: 248px;
  padding: 6px 6px 6px 8px;
  transition: 
    height var(--collapse-transition-duration) ease-in-out,
    padding var(--collapse-transition-duration) ease-in-out;
}
.collapsed .lens-switcher-content {
  height: 44px;
  padding: 6px 6px 6px 6px;
}
.lens-switcher-content .expanded-content {
  transition: opacity var(--collapse-transition-duration) ease-in-out;
  opacity: 1;
}
.collapsed .lens-switcher-content .expanded-content {
  opacity: 0;
}

/* ============================ VIEW ROUTE BUTTON CLASSES ============================ */
.view-route-btn {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 32px;
  transform: translateX(0px);
  transition: 
    width var(--collapse-transition-duration) ease-in-out,
    transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .view-route-btn {
  width: 32px;
  transform: translateX(6px);
}
.view-route-btn .label-text {
  opacity: 1;
  transition: opacity var(--collapse-transition-duration) ease-in-out;
}
.collapsed .view-route-btn .label-text {
  opacity: 0;
}
.new-badge {
  padding: 2px 6px;
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(0, 199, 169, 0.04) 0%, rgba(0, 199, 169, 0.08) 100%), rgba(0, 199, 169, 0.12);
  color: #D1F4EE;
}
.percent-badge {
  padding: 2px 6px;
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(31, 105, 255, 0.04) 0%, rgba(31, 105, 255, 0.08) 100%), rgba(31, 105, 255, 0.16);
  color: #C6E2FF;
}

/* ============================ SEARCH/CREATE NEW CLASSES ============================ */
.search-create-container {
  position: relative;
  width: 100%;
  height: 32px;
  transform: translateX(0px);
  margin-top: 6px;
  z-index: 1;
  transition: 
    width var(--collapse-transition-duration) ease-in-out,
    height var(--collapse-transition-duration) ease-in-out,
    margin-top var(--collapse-transition-duration) ease-in-out,
    transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .search-create-container {
  width: 32px;
  height: 68px;
  margin-top: 4px;
  transform: translateX(6px);
}
.search-outer-container {
  position: absolute;
  overflow: hidden;
  left: 0px;
  top: 0px;
  width: 216px;
  transition: width var(--collapse-transition-duration) ease-in-out;
}
.collapsed .search-outer-container {
  width: 32px;
  cursor: pointer;
}
.search-input {
  color: white;
  caret-color: white;

  /* Remove default browser styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  background-color: transparent;
  outline: none;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.search-input:focus {
  /* Remove default browser styles */
  outline: none;
  border: none;
  box-shadow: none;
}
.search-input::placeholder {
  transition: color 100ms ease-in-out;
  color: rgba(255, 255, 255, 0.68);
}
.search-input:hover::placeholder {
  color: white;
}
.search-input:focus::placeholder {
  color: rgba(255, 255, 255, 0.28);
}
.collapsed .search-input::placeholder {
  transition: color var(--collapse-transition-duration) ease-in-out;
  color: rgba(255, 255, 255, 0);
}
.create-new-button {
  position: absolute;
  right: 0px;
  top: 2px;
  width: 28px;
  height: 28px;
  color: rgba(255, 255, 255, 0.68);
  background-color: transparent;
  transform: translate(0px, 0px);
  transition: 
    color 100ms ease-in-out, 
    background-color 100ms ease-in-out,
    width var(--collapse-transition-duration) ease-in-out,
    height var(--collapse-transition-duration) ease-in-out,
    transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .create-new-button {
  width: 32px;
  height: 32px;
  transform: translate(0px, 34px);
}
.create-new-button:hover, .create-new-button.active {
  color: white;
  background-color: rgba(255, 255, 255, 0.12);
}
.create-new-button.anim-collapse {
  animation: createNewButtonCollapse var(--collapse-transition-duration) ease-in-out forwards;
}
@keyframes createNewButtonCollapse {
  0% { opacity: 1; }
  50% { opacity: 0.25; }
  100% { opacity: 1; }
}
.create-entity-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
}

/* ============================ FOLDERS CLASSES ============================ */
.folders-container {
  width: 248px;
  /* Fade out folders content 64px from the bottom and 8px from the top */
  mask-image: 
    linear-gradient(to top, transparent, transparent 8px, black 64px, black),
    linear-gradient(to bottom, transparent, black 8px, black);
  mask-composite: intersect;
  opacity: 1;
  transform: translateX(0px);
  transition:
    opacity var(--collapse-transition-duration) ease-in-out,
    transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .folders-container {
  transform: translateX(6px);
  opacity: 0;
  pointer-events: none;
}

/* ============================= TRIAL COUNTDOWN CLASSES ===================================== */
.trial-outer-container {
  position: relative;
  width: 100%;
  height: 116px;
  margin-bottom: 8px;
  transition: width var(--collapse-transition-duration) ease-in-out;
}
.collapsed .trial-outer-container {
  width: 0px;
}
.trial-inner-container {
  position: absolute;
  top: 0px;
  left: 0px;
  width: calc(100% + 20px);
  height: 100%;
  overflow: hidden;
  mask-image: linear-gradient(to left, transparent, black 20px, black);
}
.trial-counter {
  width: 248px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgba(15, 16, 25, 0.68);
  backdrop-filter: blur(40px);
  transition: opacity var(--collapse-transition-duration) ease-in-out;
}
.collapsed .trial-counter {
  opacity: 0;
}

/* ===================== SIDEBAR FOOTER (User Avatar & Changelog Button) ===================== */
.sidebar-footer {
  position: relative;
  width: 100%;
  height: 32px;
  transition: height var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-footer {
  height: 72px;
}
.sidebar-footer .avatar {
  position: absolute;
  left: 0px;
  bottom: 2px;
  transform: translateX(0px);
  transition: transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-footer .avatar {
  transform: translateX(8px);
}
.sidebar-footer .changelog {
  position: absolute;
  right: 0px;
  bottom: 0px;
  transform: translate(0px, 0px);
  transition: transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .sidebar-footer .changelog {
  transform: translate(-6px, -38px);
}
.user-popover-container {
  position: absolute;
  bottom: 44px;
  left: 0px;
  width: 248px;
  z-index: 1001;
  transform: translate(0px, 0px);
  transition: transform var(--collapse-transition-duration) ease-in-out;
}
.collapsed .user-popover-container {
  transform: translate(48px, 44px);
}

/* ============================= MISC CLASSES ===================================== */
.lens-switcher-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  z-index: 1002;
}
.upgrade-btn {
  background-color: rgba(0, 0, 0, 0.24);
  transition: background-color 150ms ease-in-out;
}
.upgrade-btn:hover {
  background-color: rgba(0, 0, 0, 0.32);
}
.past-due-blur-overlay {
  filter: blur(4px);
  -webkit-filter: blur(4px);
  pointer-events: none;
}

/* Changelog Indicator */
.changelog-ping {
  position: absolute;
  left: calc(100% - 10px);
  bottom: calc(100% - 10px);
  width: 9px;
  height: 9px;
  border-radius: 100%;
}
.changelog-ping::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 9px;
  height: 9px;
  background-color: inherit;
  /* transform-origin: center; */
  animation: ping 2.5s linear infinite;
  border-radius: 100%;
}

@keyframes ping {
  0% {
    transform: scale(1);
    opacity: 0.75;
  }
  50% {
    transform: scale(2);
    opacity: 0;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Vue transitions and fade in anim */
.v-enter-active, .v-leave-active,
.create-entity-enter-active, .create-entity-leave-active {
  transition: opacity 100ms ease-in-out;
}
.collapsed-create-entity-enter-active, .collapsed-create-entity-leave-active {
  transition: opacity var(--collapse-transition-duration) ease-in-out;
}
.quick-fade-enter-active, .quick-fade-leave-active {
  transition: opacity 60ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to, 
.quick-fade-enter-from, .quick-fade-enter, .quick-fade-leave-to,
.create-entity-enter-from, .create-entity-enter, .create-entity-leave-to,
.collapsed-create-entity-enter-from, .collapsed-create-entity-enter, .collapsed-create-entity-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from, 
.quick-fade-enter-to, .quick-fade-leave-from,
.create-entity-enter-to, .create-entity-leave-from,
.collapsed-create-entity-enter-to, .collapsed-create-entity-leave-from {
  opacity: 1;
}
.fade-mount-enter-active {
  animation: fade-in 100ms ease-in-out forwards;
}
.fade-mount-leave-active {
  display: none;
}
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.fade-in {
  animation: fade-in 100ms ease-in-out forwards;
}

.lock-overlay{
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  opacity: 44%;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.68);
  z-index: 50;
  transition: opacity 150ms ease-in-out;
}
</style>
